import { Elements, ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import { useContext, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import type {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { GOOGLE_PAY_PAYMENT_CONFIG } from '@/constants/stripe';
import { PaymentMethodEnum, Templates } from '@/types/common';
import { useTranslate } from '@/hooks/useTranslate';
import { mixpanelService } from '@/services/mixpanelService';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { Loader } from '../Common/Loader';
import { LoaderPayButton } from '../LoaderPayButton/LoaderPayButton';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';
interface GooglePayButtonUIProps {
  isLoading: boolean;
  onReady: () => void;
  onConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void;
  onLoadError?: (error?: unknown) => void;
}

const GooglePayButtonUI = ({ isLoading, onReady, onConfirm, onLoadError }: GooglePayButtonUIProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { setError },
    payment: { isFormValid },
    customer,
  } = context;
  const { t } = useTranslate();

  const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
    if (isFormValid) {
      trackAndNotifyPaymentClick(context, PaymentMethodEnum.GOOGLE_PAY);
      event.resolve({ emailRequired: true });
    } else {
      const errorMessageKey = getZipCodeErrorMessageKey(customer);
      setError(t(errorMessageKey));
      event.reject();
    }
  };

  return (
    <div className="flex flex-col w-full min-h-11">
      <div className={`transition-opacity duration-500 ${!isLoading && 'hidden'}`}>
        <LoaderPayButton type={PaymentMethodEnum.GOOGLE_PAY} />
      </div>
      <div className={`transition-opacity duration-500 ${isLoading && 'hidden'}`}>
        <ExpressCheckoutElement
          onClick={handleClick}
          onConfirm={onConfirm}
          onReady={onReady}
          onLoadError={onLoadError}
          options={GOOGLE_PAY_PAYMENT_CONFIG}
        />
      </div>
    </div>
  );
};

const GooglePayButton = () => {
  const context = useContext(CheckoutContext);
  const {
    actions: { runPayment, setError },
    template,
  } = context;
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(true);
  const isOnePayment = template.type === Templates.OnePayment;
  const { t } = useTranslate();

  const handleReady = () => {
    setTimeout(() => setIsLoading(false), 1000);
  };

  const handleLoadError = (error?: unknown) => {
    console.error('Google Pay load error:', error);
    if (error && isOnePayment) {
      mixpanelService.track(
        MixpanelEvents.PAYMENT_NOT_SUPPORTED,
        getMixpanelProperties(context) as Record<string, unknown>
      );
      setError(t('paymentMethodNotAvailable'));
    }
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    try {
      await runPayment({ event, elements: elements! }, PaymentMethodEnum.GOOGLE_PAY);
    } catch {
      setError(t('somethingWentWrong'));
    }
  };

  return (
    <GooglePayButtonUI
      isLoading={isLoading}
      onReady={handleReady}
      onConfirm={handleConfirm}
      onLoadError={handleLoadError}
    />
  );
};

export const GooglePayStripeForm = () => {
  const context = useContext(CheckoutContext);
  const {
    pricing,
    actions: { setError },
    template,
  } = context;
  const isOnePayment = template.type === Templates.OnePayment;
  const { t } = useTranslate();
  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({
    paymentMethod: PaymentMethodEnum.GOOGLE_PAY,
    isExpress: true,
  });

  if (isStripeLoading || !stripe) {
    return <Loader />;
  }

  try {
    return (
      <Elements
        stripe={stripe}
        options={{
          mode: 'payment',
          amount: Math.round(pricing.priceBreakdown.priceTotal),
          currency: pricing.currencyCode.toLowerCase(),
          loader: 'never',
          paymentMethodCreation: 'manual',
        }}
      >
        <GooglePayButton />
      </Elements>
    );
  } catch (error) {
    console.error('Google Pay Stripe Elements initialization error:', error);
    if (isOnePayment) {
      mixpanelService.track(
        MixpanelEvents.PAYMENT_NOT_SUPPORTED,
        getMixpanelProperties(context) as Record<string, unknown>
      );
      setError(t('paymentMethodNotAvailable'));
    }
    return null;
  }
};
